import { createAsyncThunk } from "@reduxjs/toolkit";
import apiAxios from "../../../services/apiAxios";
import toast from "react-hot-toast";
import {
  setAppointments,
  setQueueStats,
  setDoctorSchedule,
  setPatientSearchResults,
  setAppointmentLoading,
  setAppointmentError,
  updateSingleAppointmentInState,
} from "../../Reducer/Appointment/AppointmentSlice";

// Fetch Appointments
export const fetchAppointments = createAsyncThunk(
  "appointment/fetchAppointments",
  async (params = {}, { dispatch }) => {
    dispatch(setAppointmentLoading(true));
    try {
      const response = await apiAxios.get("api/appointment/list", { params });
      if (response?.data?.success) {
        dispatch(setAppointments(response.data.appointments));
      }
      dispatch(setAppointmentLoading(false));
      return response?.data?.appointments;
    } catch (error) {
      dispatch(setAppointmentError(error?.response?.data?.message || error.message));
      dispatch(setAppointmentLoading(false));
      throw error;
    }
  }
);

// Fetch Queue Stats
export const fetchQueueStats = createAsyncThunk(
  "appointment/fetchQueueStats",
  async (date, { dispatch }) => {
    try {
      const response = await apiAxios.get("api/appointment/queue-stats", {
        params: { date },
      });
      if (response?.data?.success) {
        dispatch(setQueueStats(response.data.stats));
      }
    } catch (error) {
      console.error("Error fetching queue stats:", error);
    }
  }
);

// Create Appointment
export const createAppointmentThunk = createAsyncThunk(
  "appointment/createAppointment",
  async (payload, { dispatch }) => {
    const loadingToast = toast.loading("Creating appointment...");
    try {
      const response = await apiAxios.post("api/appointment/create", payload);
      toast.dismiss(loadingToast);
      if (response?.data?.success) {
        toast.success(`Appointment #${response.data.appointment.tokenNumber} booked!`);
        // Refresh list and stats
        dispatch(fetchAppointments({ date: payload.appointmentDate }));
        dispatch(fetchQueueStats(payload.appointmentDate));
        return response.data.appointment;
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error?.response?.data?.message || "Failed to create appointment");
      throw error;
    }
  }
);

// Update Appointment Status
export const updateAppointmentStatusThunk = createAsyncThunk(
  "appointment/updateStatus",
  async ({ AID, status, cancelledReason, appointmentDate }, { dispatch }) => {
    try {
      const response = await apiAxios.patch(`api/appointment/status/${AID}`, {
        status,
        cancelledReason,
      });
      if (response?.data?.success) {
        dispatch(updateSingleAppointmentInState(response.data.appointment));
        toast.success(`Status updated to ${status.replace("_", " ")}`);
        if (appointmentDate) {
          dispatch(fetchAppointments({ date: appointmentDate }));
          dispatch(fetchQueueStats(appointmentDate));
        }
        return response.data.appointment;
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
      throw error;
    }
  }
);

// Update Payment Status
export const updatePaymentStatusThunk = createAsyncThunk(
  "appointment/updatePayment",
  async ({ AID, paymentStatus, paymentMethod, consultationFee, receiptId, appointmentDate }, { dispatch }) => {
    try {
      const response = await apiAxios.patch(`api/appointment/payment/${AID}`, {
        paymentStatus,
        paymentMethod,
        consultationFee,
        receiptId,
      });
      if (response?.data?.success) {
        dispatch(updateSingleAppointmentInState(response.data.appointment));
        toast.success("Payment details updated!");
        if (appointmentDate) {
          dispatch(fetchQueueStats(appointmentDate));
        }
        return response.data.appointment;
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update payment");
      throw error;
    }
  }
);

// Reschedule Appointment
export const rescheduleAppointmentThunk = createAsyncThunk(
  "appointment/reschedule",
  async ({ AID, appointmentDate, slotStartTime, slotEndTime, selectedDate }, { dispatch }) => {
    const loadingToast = toast.loading("Rescheduling appointment...");
    try {
      const response = await apiAxios.patch(`api/appointment/reschedule/${AID}`, {
        appointmentDate,
        slotStartTime,
        slotEndTime,
      });
      toast.dismiss(loadingToast);
      if (response?.data?.success) {
        toast.success(`Rescheduled to ${appointmentDate} at ${slotStartTime}!`);
        dispatch(updateSingleAppointmentInState(response.data.appointment));
        if (selectedDate) {
          dispatch(fetchAppointments({ date: selectedDate }));
          dispatch(fetchQueueStats(selectedDate));
        }
        return response.data.appointment;
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error?.response?.data?.message || "Failed to reschedule appointment");
      throw error;
    }
  }
);

// Fetch Doctor Schedule
export const fetchDoctorSchedule = createAsyncThunk(
  "appointment/fetchSchedule",
  async (_, { dispatch }) => {
    try {
      const response = await apiAxios.get("api/appointment/schedule");
      if (response?.data?.success) {
        dispatch(setDoctorSchedule(response.data.schedule));
      }
    } catch (error) {
      console.error("Error fetching schedule:", error);
    }
  }
);

// Save Doctor Schedule
export const saveDoctorScheduleThunk = createAsyncThunk(
  "appointment/saveSchedule",
  async (payload, { dispatch }) => {
    const loadingToast = toast.loading("Saving schedule settings...");
    try {
      const response = await apiAxios.post("api/appointment/schedule", payload);
      toast.dismiss(loadingToast);
      if (response?.data?.success) {
        toast.success("Schedule settings saved!");
        dispatch(setDoctorSchedule(response.data.schedule));
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Failed to save schedule settings");
    }
  }
);

// Search Patients
export const searchPatientsThunk = createAsyncThunk(
  "appointment/searchPatients",
  async (query, { dispatch }) => {
    try {
      const response = await apiAxios.get("api/appointment/patients/search", {
        params: { query },
      });
      if (response?.data?.success) {
        dispatch(setPatientSearchResults(response.data.patients));
      }
    } catch (error) {
      console.error("Error searching patients:", error);
    }
  }
);
